import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Button } from '../components/ui/button'
import { Label } from '../components/ui/label'
import { toast } from 'sonner'
import { ArrowLeft, Mail, Loader2, CheckCircle2 } from 'lucide-react'

const API_URL = process.env.REACT_APP_BACKEND_URL

export default function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!email) {
      toast.error('Please enter your email address')
      return
    }
    
    setLoading(true)
    
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase() })
      })
      
      const data = await response.json()
      
      if (data.success) {
        setSent(true)
        toast.success('Reset link sent!')
      } else {
        toast.error(data.detail || 'Something went wrong')
      }
    } catch (err) {
      toast.error('Failed to send reset link. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background to-secondary/20">
      <div className="w-full max-w-md space-y-6">
        {/* Logo */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl overflow-hidden mb-4">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/d9a36397-75b6-45a0-b476-0118e30707e5/images/7feac39be335da8e6a2ca874b553dd78b4b011049f274c8c33789ba32c56e471.png" 
              alt="Driver PNL Logo" 
              className="w-full h-full object-cover"
            />
          </div>
          <h1 className="text-3xl font-bold tracking-tight font-['Barlow_Condensed']">
            DRIVER PNL
          </h1>
        </div>

        <Card className="glass">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl font-['Barlow_Condensed']">
              {sent ? 'CHECK YOUR EMAIL' : 'FORGOT PASSWORD'}
            </CardTitle>
            <CardDescription>
              {sent 
                ? 'We sent you a password reset link' 
                : 'Enter your email and we\'ll send you a reset link'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="space-y-6 py-4">
                <div className="flex justify-center">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-green-500" />
                  </div>
                </div>
                <div className="text-center space-y-2">
                  <p className="text-muted-foreground">
                    If an account exists for <strong>{email}</strong>, you'll receive an email with a link to reset your password.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    The link will expire in 60 minutes.
                  </p>
                </div>
                <div className="space-y-3">
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => { setSent(false); setEmail(''); }}
                  >
                    Send to a different email
                  </Button>
                  <Link to="/login" className="block">
                    <Button variant="ghost" className="w-full">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Sign In
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12 pl-10"
                      data-testid="forgot-email-input"
                      disabled={loading}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 rounded-full font-semibold"
                  disabled={loading}
                  data-testid="forgot-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
                
                <Link to="/login" className="block">
                  <Button variant="ghost" className="w-full">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Sign In
                  </Button>
                </Link>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
